import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import { useCallback, type ChangeEvent } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, AlignLeftIcon, AlignCenterIcon, AlignRightIcon, LinkIcon, ImageIcon } from './icons';

export function Editor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: true, autolink: true }),
      Image,
      Highlight,
      TextStyle,
      FontFamily.configure({ types: ['textStyle'] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'tiptap min-h-[240px] border rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-200'
      }
    }
  });

  const setLink = useCallback(() => {
    const url = window.prompt('Bağlantı URL');
    if (!url) return;
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    const url = window.prompt('Görsel URL');
    if (!url) return;
    editor?.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const currentFontFamily = editor?.getAttributes('textStyle').fontFamily ?? '';
  const currentFontSize = editor?.getAttributes('textStyle').fontSize ?? '';

  const onFontFamilyChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    if (!editor) return;
    const value = event.target.value;
    const chain = editor.chain().focus();
    if (!value) {
      chain.unsetFontFamily().run();
    } else {
      chain.setFontFamily(value).run();
    }
  }, [editor]);

  const onFontSizeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    if (!editor) return;
    const value = event.target.value;
    const chain = editor.chain().focus();
    if (!value) {
      chain.updateAttributes('textStyle', { fontSize: null }).run();
    } else {
      chain.setTextStyle({ fontSize: value } as any).run();
    }
  }, [editor]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button type="button" className={`btn-secondary ${editor?.isActive('bold') ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().toggleBold().run()} aria-label="Kalın" aria-pressed={editor?.isActive('bold') || false}>
          <BoldIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive('italic') ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().toggleItalic().run()} aria-label="İtalik" aria-pressed={editor?.isActive('italic') || false}>
          <ItalicIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive('underline') ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().toggleUnderline().run()} aria-label="Altı çizili" aria-pressed={editor?.isActive('underline') || false}>
          <UnderlineIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive({ textAlign: 'left' }) ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().setTextAlign('left').run()} aria-label="Sola hizala" aria-pressed={editor?.isActive({ textAlign: 'left' }) || false}>
          <AlignLeftIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive({ textAlign: 'center' }) ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().setTextAlign('center').run()} aria-label="Ortala" aria-pressed={editor?.isActive({ textAlign: 'center' }) || false}>
          <AlignCenterIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive({ textAlign: 'right' }) ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().setTextAlign('right').run()} aria-label="Sağa hizala" aria-pressed={editor?.isActive({ textAlign: 'right' }) || false}>
          <AlignRightIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive('link') ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={setLink} aria-label="Bağlantı" aria-pressed={editor?.isActive('link') || false}>
          <LinkIcon />
        </button>
        <button type="button" className="btn-secondary" onClick={addImage} aria-label="Görsel">
          <ImageIcon />
        </button>
        <button type="button" className={`btn-secondary ${editor?.isActive('highlight') ? '!bg-brand-100 !text-brand-800' : ''}`} onClick={() => editor?.chain().focus().toggleHighlight().run()} aria-label="Vurgula" aria-pressed={editor?.isActive('highlight') || false}>
          <span className="text-xs font-medium">Vurgula</span>
        </button>
        <select className="input !w-auto" value={currentFontFamily} onChange={onFontFamilyChange}>
          <option value="">Yazı tipi</option>
          <option value="Inter">Inter</option>
          <option value="Merriweather">Merriweather</option>
          <option value="Georgia">Georgia</option>
          <option value="Arial">Arial</option>
        </select>
        <select className="input !w-auto" value={currentFontSize} onChange={onFontSizeChange}>
          <option value="">Boyut</option>
          <option value="14px">14</option>
          <option value="16px">16</option>
          <option value="18px">18</option>
          <option value="20px">20</option>
          <option value="24px">24</option>
        </select>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
